import React from "react";
import { DayPicker } from "react-day-picker";
import { CalIcon } from "./icons";
import { format, differenceInCalendarDays } from "date-fns";

/**
 * DatesSection — the DayPicker range calendar, with:
 *  - "locked" placeholder when destination isn't picked yet
 *  - "summary card" when both dates chosen + calendarOpen is false (edit link toggles it back)
 *  - the DayPicker itself, honoring admin-blackout dates + surge notice
 */
export const DatesSection = ({
  destinationSet,
  range,
  onRangeChange,
  blackoutDates,
  activeSurge,
  calendarOpen,
  onOpenCalendar,
}) => {
  const datesSet = !!(range?.from && range?.to);

  if (!destinationSet) {
    return (
      <div className="mt-6 pt-6 border-t border-[#FFD166]/10 text-center text-[11px] text-[#B9C8D5]/60" data-testid="trip-setup-dates-locked">
        <CalIcon className="w-4 h-4 text-[#FFD166]/40 mx-auto mb-1" />
        Pick your delivery address above to unlock dates.
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-[#FFD166]/10" data-testid="trip-setup-dates-section">
      <div className="flex items-center justify-between mb-3">
        <label className="eyebrow flex items-center gap-2">
          <CalIcon className="w-3.5 h-3.5 text-[#FFD166]" /> 2 · Rental dates
          {datesSet && <span className="text-emerald-400 text-[11px]" data-testid="dates-step-check">✓</span>}
        </label>
        {datesSet && !calendarOpen && (
          <button type="button" onClick={onOpenCalendar} className="text-[11px] text-[#FFD166] hover:underline" data-testid="trip-setup-edit-dates">Change dates</button>
        )}
      </div>

      {datesSet && !calendarOpen ? (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-sm text-[#F6F1E4] flex items-center gap-3" data-testid="trip-setup-dates-summary">
          <CalIcon className="w-4 h-4 text-[#FFD166] shrink-0" />
          <div className="flex-1">
            <div>
              <span className="text-[#FFD166] font-medium">{format(range.from, "MMM d")}</span> →{" "}
              <span className="text-[#FFD166] font-medium">{format(range.to, "MMM d, yyyy")}</span>
            </div>
            <div className="text-[11px] text-[#B9C8D5]">
              {differenceInCalendarDays(range.to, range.from) + 1} days
              {differenceInCalendarDays(range.to, range.from) + 1 < 3 && " · billed as 3-day minimum"}
            </div>
            {activeSurge && (
              <div className="text-[11px] text-[#F4A93A] mt-1" data-testid="surge-notice">
                ✨ {activeSurge.name} pricing applies (+{Number(activeSurge.percentage || 0).toFixed(1)}%)
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-center">
            <div className="rdp-themed">
              <DayPicker
                mode="range"
                selected={range}
                onSelect={(r, day) => {
                  // Once a full range exists, DayPicker's default click behaviour
                  // stretches/shrinks it — which made changing dates take 3 tries.
                  // Instead, any click on a completed range starts a fresh one.
                  if (range?.from && range?.to && day) {
                    onRangeChange({ from: day, to: undefined });
                    return;
                  }
                  onRangeChange(r || { from: undefined, to: undefined });
                }}
                numberOfMonths={1}
                fromDate={new Date()}
                disabled={blackoutDates.length > 0 ? blackoutDates : undefined}
                modifiers={blackoutDates.length > 0 ? { blackout: blackoutDates } : undefined}
                modifiersClassNames={{ blackout: "rdp-blackout" }}
                data-testid="trip-setup-daypicker"
              />
            </div>
          </div>
          {blackoutDates.length > 0 && (
            <p className="mt-2 text-center text-[10px] text-[#B9C8D5]/70" data-testid="blackout-legend">
              Greyed-out days are booked out or closed — please pick a different range.
            </p>
          )}
          {range?.from && !range?.to && (
            <p className="mt-2 text-center text-[11px] text-[#B9C8D5]">Now pick your <span className="text-[#FFD166]">return</span> date.</p>
          )}
        </>
      )}
    </div>
  );
};

export default DatesSection;
